import React, { useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Form } from 'react-bootstrap';
import { HubConnectionBuilder, HubConnectionState, HttpTransportType, LogLevel } from '@microsoft/signalr';
import { usePermissions } from '../../hooks/usePermissions';
import SignalRTestTable from './SignalRTestTable';
import { validateSignalREndpoint as validateEndpoint } from '../../realtime/endpoint';

const EVENTS = ['ReceiveAction', 'DomainChanged', 'ProjectUpdated', 'ProjectDeleted', 'CommentAdded'];
const safeError = (error) => String(error?.message || error)
  .replace(/https?:\/\/[^\s"']+/gi, '[endpoint]')
  .replace(/eyJ[\w-]+\.[\w-]+\.[\w-]+/g, '[token]');

export default function AdminSignalRTest() {
  const { canEdit } = usePermissions();
  const allowSend = canEdit('admin.signalr-test');
  const [endpoint, setEndpoint] = useState(process.env.REACT_APP_SIGNALR_API_URL || '');
  const [transport, setTransport] = useState('auto');
  const [status, setStatus] = useState('Disconnected');
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(false);
  const [logs, setLogs] = useState([]);
  const connection = useRef(null);
  const mounted = useRef(false);
  const probe = useRef(null);
  const pendingEcho = useRef(null);

  const log = (message) => {
    if (mounted.current) setLogs((items) => [...items.slice(-199), { time: new Date().toLocaleTimeString(), message }]);
  };
  const clearEcho = () => {
    clearTimeout(pendingEcho.current?.timer);
    pendingEcho.current = null;
    if (mounted.current) setPending(false);
  };

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      probe.current?.abort();
      clearTimeout(pendingEcho.current?.timer);
      const current = connection.current;
      connection.current = null;
      current?.stop().catch(() => {});
    };
  }, []);

  const checkNegotiation = async () => {
    setBusy(true);
    const controller = new AbortController();
    probe.current = controller;
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const base = validateEndpoint(endpoint);
      const response = await fetch(`${base}/negotiate?negotiateVersion=1`, {
        method: 'POST', credentials: 'include', signal: controller.signal,
      });
      log(`Negotiation HTTP ${response.status}.`);
      if (!response.ok) throw new Error('Negotiation rejected. Check Function access restrictions, authentication, and CORS.');
      const data = await response.json();
      if (!data?.url || !data?.accessToken) throw new Error('Expected a JSON object with url and accessToken.');
      const service = new URL(data.url);
      log(`Connection information received for ${service.hostname}; hub ${service.searchParams.get('hub') || '(unspecified)'}. Token omitted.`);
    } catch (error) {
      log(`Negotiation failed: ${safeError(error)} Browser network errors can indicate CORS; inspect the Network panel status.`);
    } finally {
      clearTimeout(timeout);
      probe.current = null;
      if (mounted.current) setBusy(false);
    }
  };

  const connect = async () => {
    setBusy(true);
    setStatus('Connecting');
    try {
      const base = validateEndpoint(endpoint);
      const options = { withCredentials: true, timeout: 15000 };
      if (transport !== 'auto') options.transport = HttpTransportType[transport];
      // Start at Functions, so reconnects can negotiate fresh Azure credentials.
      const current = new HubConnectionBuilder().withUrl(base, options)
        .withAutomaticReconnect([0, 2000, 10000, 30000])
        .configureLogging(LogLevel.None).build();
      connection.current = current;
      const isCurrent = () => mounted.current && connection.current === current;
      EVENTS.forEach((event) => current.on(event, (action) => {
        if (!isCurrent()) return;
        log(`Received ${event}. Payload omitted.`);
        if (pendingEcho.current && event === 'ReceiveAction' && action?.type === 'signalr/diagnostic' &&
          action?.payload?.correlationId === pendingEcho.current?.id) {
          log(`Round trip confirmed in ${Date.now() - pendingEcho.current.started} ms.`);
          clearEcho();
        }
      }));
      current.onreconnecting(() => {
        if (!isCurrent()) return;
        setStatus('Reconnecting');
        clearEcho();
        log('Connection lost. Reconnecting with fresh negotiation.');
      });
      current.onreconnected(() => {
        if (!isCurrent()) return;
        setStatus('Connected');
        log('Reconnected.');
      });
      current.onclose((error) => {
        if (!isCurrent()) return;
        setStatus('Disconnected');
        clearEcho();
        log(error ? `Connection closed: ${safeError(error)}` : 'Disconnected.');
      });
      await current.start();
      if (isCurrent()) {
        setStatus('Connected');
        log('Connected. Awaiting events; connection alone does not verify the upstream handler.');
      } else await current.stop();
    } catch (error) {
      const current = connection.current;
      connection.current = null;
      await current?.stop().catch(() => {});
      if (mounted.current) setStatus('Disconnected');
      log(`Connect failed: ${safeError(error)}`);
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try { await connection.current?.stop(); }
    catch (error) { log(`Disconnect failed: ${safeError(error)}`); }
    finally { if (mounted.current) setBusy(false); }
  };

  const sendDiagnostic = async () => {
    const current = connection.current;
    if (!allowSend || current?.state !== HubConnectionState.Connected || pendingEcho.current) return;
    const id = crypto.randomUUID();
    const timer = setTimeout(() => {
      log('No matching echo after 15 seconds. Check upstream routing, trigger Arguments, and output binding.');
      clearEcho();
    }, 15000);
    pendingEcho.current = { id, timer, started: Date.now() };
    setPending(true);
    try {
      await current.send('SendAction', { type: 'signalr/diagnostic', payload: { correlationId: id } });
      log('Diagnostic sent. Waiting for ReceiveAction to confirm delivery through the Function.');
    } catch (error) {
      clearEcho();
      log(`Send failed: ${safeError(error)}`);
    }
  };

  const disconnected = status === 'Disconnected';
  return <Card>
    <Card.Body>
      <div className="d-flex align-items-center gap-3 mb-3">
        <h2 className="mb-0">SignalR Test</h2>
        <Badge bg={status === 'Connected' ? 'success' : 'secondary'}>{status}</Badge>
      </div>
      <Alert variant="info">Connect to a test Functions app, then send a diagnostic to verify the complete round trip.
        The diagnostic broadcasts to connected clients on projectHub. This page does not update jobs or dispatch received actions into Redux.</Alert>
      <Form.Group controlId="signalr-endpoint" className="mb-3">
        <Form.Label>SignalR Functions base URL</Form.Label>
        <Form.Control value={endpoint} onChange={(event) => setEndpoint(event.target.value)} disabled={busy || !disconnected}
          placeholder="https://your-signalr-test-app.azurewebsites.net/api" />
        <Form.Text>Use the app that hosts negotiate. This may be different from the portal API.</Form.Text>
      </Form.Group>
      <Form.Group controlId="signalr-transport" className="mb-3" style={{ maxWidth: 320 }}>
        <Form.Label>Transport</Form.Label>
        <Form.Select value={transport} onChange={(event) => setTransport(event.target.value)} disabled={busy || !disconnected}>
          <option value="auto">Automatic</option><option value="WebSockets">WebSockets</option>
          <option value="ServerSentEvents">Server-sent events</option><option value="LongPolling">Long polling</option>
        </Form.Select>
      </Form.Group>
      <div className="d-flex flex-wrap gap-2 mb-3">
        <Button variant="outline-primary" disabled={busy || !disconnected || !endpoint.trim()} onClick={checkNegotiation}>Check Negotiation</Button>
        <Button disabled={busy || !disconnected || !endpoint.trim()} onClick={connect}>Connect</Button>
        <Button variant="outline-secondary" disabled={busy || disconnected} onClick={disconnect}>Disconnect</Button>
        <Button variant="outline-primary" disabled={!allowSend || busy || pending || status !== 'Connected'} onClick={sendDiagnostic}>
          {pending ? 'Waiting for Echo…' : 'Send Diagnostic'}
        </Button>
        <Button variant="outline-secondary" onClick={() => setLogs([])}>Clear Log</Button>
      </div>
      <p className="text-muted small">Open this page in two tabs and connect both to confirm cross-client delivery. Leaving this tab disconnects.
        Logs retain the latest 200 entries and omit tokens and event payloads. Authentication uses browser cookies; API bearer authentication requires an API-specific token integration.</p>
      <SignalRTestTable connection={status === 'Connected' ? connection.current : null} canEdit={allowSend} />
      <pre aria-label="SignalR diagnostic log" aria-live="polite" className="bg-light border rounded p-3" style={{ maxHeight: 400, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
        {logs.length ? logs.map((entry) => `${entry.time} — ${entry.message}`).join('\n') : 'No diagnostics yet.'}
      </pre>
    </Card.Body>
  </Card>;
}
